/*
How to write routes:
    1. Standard options are covered here: https://router.vuejs.org/en/
    2. Meta option:
     - title - string, allows to add prefix to page title.
     - permission - function, get session as parameter. Should return true if
                    user has access to the page. The page will be available for
                    anyone if permission is not defined.
     - redirect - function, return path where user should be redirected.
                  Redirect is executed only if user does not have access to
                  the URL.

Route example:

    const orgRoute = {
        // standard vue-router options
        name: 'account',
        path: '/org/:id?',
        component: require('./account/component.vue'),

        // custom meta options
        meta: {
            title: 'Account', // prefix will be added to page title.
            permission: (session) => session.isSuperuser, // page is available only for researchers
            redirect: redirectCustomerToSegments,  // if customer tries to reach this page
                                                   // he will be redirected to /segments URL
        },
    };

Check router.beforeEach and router.afterEach hooks to understand how functions
from meta are executed.
*/
Vue.use(VueRouter);

import store from './base/store';
import auth from './base/index';
import utils from './utils/company-domain';

function redirectCustomerToSegments(session) {
    if (session.isCustomer) return '/signin';
}

const signinRoute = {
    name: 'signin',
    path: '/signin',
    beforeEnter: (to) => auth.openLoginPage(to),
};

import ViewerComponent from './viewer/component.vue';
const viewerRoute = {
    name: 'viewer',
    path: '/data/:sourceName',
    component: ViewerComponent,
    meta: {
        title: 'Data',
        permission: (session) => session.isSuperuser,
        redirect: redirectCustomerToSegments,
        props: { bootstrap: true },
    },
};

import UserComponent from './customer/user.vue';
import UserProfileComponent from './customer/profile.vue';

const userRoute = {
    name: 'user',
    path: '/account',
    component: UserComponent,
    meta: {
        title: 'Settings',
        props: { bootstrap: true }
    },
    children: [{
        name: 'user-profile',
        path: 'profile',
        component: UserProfileComponent,
        meta: {
            title: 'Profile - Settings',
            permission: (session) => session.isCustomer || session.isSuperuser,
            props: { bootstrap: true }
        }
    }]
};

import NotFoundComponent from './404.vue';
const router = new VueRouter({
    routes: [
        signinRoute,
        userRoute,
        viewerRoute,
        {
            path: '/',
            redirect: () => {
                if (store.state.session && store.state.session.isSuperuser) {
                    return '/data/orgs';
                } else {
                    return '/signin';
                }
            }
        },
        {
            path: '*',
            component: NotFoundComponent
        }
    ],
    mode: 'history'
});

function checkPermissions(to, session) {
    const permission = (to.meta || {}).permission;
    return !permission || permission(session);
}

function getRedirect(to, session) {
    const redirect = (to.meta || {}).redirect;
    if (redirect) return redirect(session);
}

// TODO: Move this into a separate module/
function initPendo(session) {
    // to bulletproof code make sure that session exists
    if (!session) return;
    // pendo should be initialized for customers only
    //if (!session.isCustomer) return;
    // do not track internal users
    const email = session.original_user_email || session.email;
    const excludeSuffix = '@leadgenius.com';
    if (email && email.indexOf(excludeSuffix) > 0) return;

    // skip initialization if pendo is already initialized
    if (pendo.isInit) return;
    let companyName = null;
    if (session.user_profile && session.user_profile.company_name) {
        companyName = session.user_profile.company_name;
    } else {
        companyName = utils.getCompanyName(session);
    }

    var payload = {
        visitor: {
            id: session.email ? session.email : null,  // Required if user is logged in
            firstName: session.first_name ? session.first_name : null,
            lastName: session.last_name ? session.last_name : null,
            category: session.user_profile ? session.user_profile.category : null,
            isClient: session.isCustomer ? session.isCustomer : null,
            is_manager: session.is_manager ? session.is_manager : null,
            is_qa: session.is_qa ? session.is_qa : null,
            is_staff: session.is_staff ? session.is_staff : null,
            is_worker: session.is_worker ? session.is_worker : null,
            created: session.created_at ? session.created_at : null
        },
        account: {
            id: session.user_profile ? session.user_profile.id : null,
            name: companyName,
            category: session.user_profile ? session.user_profile.category : null,
            isClient: session.isCustomer
        },
        parentAccount: {
            name: companyName
        }
    };
    pendo.initialize(payload);
    pendo.isInit = true;
}

// Handle route access permissions based on route meta.
router.beforeEach(function(to, from, next) {
    // TODO: isAnonymous - is a temporary solution, we have to initiate session
    // for every user, not keep it undefined for anonymous.
    // Should be fixed in DOT-166
    const session = store.state.session || { isAnonymous: true };
    const html = document.getElementsByTagName('html')[0];
    const head = document.getElementsByTagName('head')[0];
    let milligram = document.getElementsByClassName('milligram-file');

    html.removeAttribute('id');

    if (to.meta && to.meta.props && to.meta.props.bootstrap) {
        html.setAttribute('id', 'bootstrap');
        if (milligram.length) {
            head.removeChild(milligram[0]);
        }
    } else {
        html.setAttribute('id', 'semantic-framework');
        if (!milligram.length) {
            let milligram = document.createElement('link');
            milligram.setAttribute('rel', 'stylesheet');
            milligram.setAttribute('class', 'milligram-file');
            milligram.setAttribute('href', '/styles/milligram.min.css');
            head.insertBefore(milligram, head.children[1]);
        }
    }
    // init Pendo tracking code when session becomes available
    if(process.env.NODE_ENV === 'production') {
        initPendo(session);
    }

    // Step 1: Global check to see if we should redirect out of this app
    if (session.isCustomer) {
        window.location = `${process.env.DASHBOARD_URL}`;
    }

    // Step 2: check user permissions. Let him in if he has access to the URL.
    if (checkPermissions(to, session)) return next();
    // Step 3: if specific redirect path is defined - use it.
    const redirect = getRedirect(to, session);
    if (redirect) {
        router.replace(redirect);
        return next();
    }

    // Step 4: if user is not authenticated - redirect to sign in.
    if (session.isAnonymous) {
        return router.push({ name: 'signin', params: { next: to.path }});
    }
    // Step 5: Show 404 error if user has no access to the page.
    router.replace('404');

    return next();
});

router.afterEach(function(to, from, next) {
    // Add prefix to title if it is defined in route meta.
    const titlePrefix = (to.meta || {}).title;
    document.title = titlePrefix ? titlePrefix + ' - LeadGenius' : 'LeadGenius';

    store.commit('setReady');
});

export default router;
